// Re-export all existing scribe API functions
export {
	getPack,
	getPacks,
	deletePrompt,
	hidePrompt,
	createPrompt,
	updatePrompt,
	getPrompts,
	getPrompt,
	getAnalysisStatus,
	getAnalyses,
	getAnalysesBulk,
	getAnalysis,
	getAllAnalyses,
	createAnalysis,
	deleteAnalysis,
	getAssistantTools,
	createAssistantAutomation,
	getPackPlatforms,
	preflightAnalysis,
	markdownToHtml,
	getAvailablePlatforms,
	saveTaskSelection,
	getTaskSelection,
	deleteTaskSelection,
	validateDryRun,
	cancelAnalysis
} from "@/api/scribe";

import { auth } from "@/auth";
import type {
	PackListWithFiltersResponse,
	CategoryItem,
	KnowledgeRequirement,
	KnowledgeAssignment,
	PackKnowledgeFulfillment,
	TaskRequirementPayload,
	UpdateTaskRequirementPayload,
	RequirementDefaultDataUploadRequest,
	ConfirmRequirementDefaultDataUploadRequest
} from "./types";
import { responseToError } from "./apiError";
// F-16: reuse admin's canonical presigned-PUT transport and the shared retry classification instead
// of a second, weaker upload path. See `uploadToPresignedUrl` below.
import { parseS3XmlError, putToS3WithStallTimeout } from "@/helpers/s3";
import { classifyUploadFailure, computeRetryAt } from "@uploads";

const getUrl = (path: string) => {
	return window.env.VITE_SCRIBE_URL + "/api" + path;
};

const getHeaders = async (method: string) => {
	const devToken = window.env.VITE_SCRIBE_DEV_TOKEN;
	let token: string;

	if (devToken) {
		token = devToken;
	} else {
		if (auth.runningCallback) {
			await auth.waitForCallbackToFinish();
		}
		token = await auth.getAccessToken();
	}

	const headers: Record<string, string> = {
		Authorization: "Bearer " + token
	};

	if (!["DELETE", "GET"].includes(method)) {
		headers["Content-Type"] = "application/json";
	}

	return headers;
};

// New API methods for RunAgentDialog and Templates

export interface PackFilters {
	published?: boolean;
	hasRealTimeMedia?: boolean;
	search?: string;
	platforms?: string[];
	categoryId?: string;
	categoryCodeNames?: string[];
	author?: string;
	sortBy?: "name" | "rating" | "userCount" | "createdAt" | "position";
	sortOrder?: "asc" | "desc";
	cursor?: string;
	limit?: number;
}

export const getPacksWithFilters = async (filters: PackFilters): Promise<PackListWithFiltersResponse> => {
	const params = new URLSearchParams();
	if (filters.published !== undefined) params.set("published", String(filters.published));
	if (filters.hasRealTimeMedia !== undefined) params.set("hasRealTimeMedia", String(filters.hasRealTimeMedia));
	if (filters.search) params.set("search", filters.search);
	if (filters.platforms && filters.platforms.length > 0) {
		filters.platforms.forEach((p) => params.append("platforms", p));
	}
	if (filters.categoryId) params.set("categoryId", filters.categoryId);
	if (filters.categoryCodeNames && filters.categoryCodeNames.length > 0) {
		filters.categoryCodeNames.forEach((codeName) => params.append("categoryCodeNames", codeName));
	}
	if (filters.author) params.set("author", filters.author);
	if (filters.sortBy) params.set("sortBy", filters.sortBy);
	if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
	if (filters.cursor) params.set("cursor", filters.cursor);
	if (filters.limit) params.set("limit", String(filters.limit));

	const response = await fetch(getUrl("/packs?" + params.toString()), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

/**
 * Distinct display authors across the published packs visible to the caller (LSP-16352).
 * Returns null when the endpoint does not exist yet (older scribe-api deploys), so the
 * caller can fall back to scanning pack pages.
 */
export const getPackAuthors = async (): Promise<string[] | null> => {
	const response = await fetch(getUrl("/packs/authors"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		const body = await response.json();
		return Array.isArray(body?.authors) ? body.authors : [];
	}
	if (response?.status === 404) {
		return null;
	}
	throw await responseToError(response);
};

export const installPack = async (
	id: string,
	fulfillments?: PackKnowledgeFulfillment[],
	options?: { localKnowledgeReviewed?: boolean; settings?: Record<string, unknown> }
): Promise<{ message: string; organizationInstalledPackId: string }> => {
	const body: { fulfillments?: PackKnowledgeFulfillment[]; localKnowledgeReviewed?: boolean; settings?: Record<string, unknown> } = {};
	if (fulfillments && fulfillments.length > 0) {
		body.fulfillments = fulfillments;
	}
	if (options?.localKnowledgeReviewed) {
		body.localKnowledgeReviewed = true;
	}
	if (options?.settings) {
		body.settings = options.settings;
	}
	const response = await fetch(getUrl("/packs/" + id + "/install"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(body)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const uninstallPack = async (id: string): Promise<{ message: string }> => {
	const response = await fetch(getUrl("/packs/" + id + "/install"), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const deletePack = async (id: string): Promise<{ message: string }> => {
	const response = await fetch(getUrl("/packs/" + id), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getCategories = async (): Promise<{ categories: CategoryItem[] }> => {
	const response = await fetch(getUrl("/categories"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

// Feeds the templates filter dropdown only, so the user's last good list is fine while Exchange is down.
export const getPlatforms = async (): Promise<any[]> => {
	const response = await fetch(getUrl("/tasks/platforms?allowStale=true"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		const data = await response.json();
		return data.platforms || [];
	} else {
		throw await responseToError(response);
	}
};

export const getPublisherProfileImageUrl = async (packId: string): Promise<string | null> => {
	try {
		const response = await fetch(getUrl("/publisher-profile-image/" + packId), {
			method: "GET",
			headers: await getHeaders("GET")
		});
		if (response != null && response.ok) {
			const data = await response.json();
			return data.imageUrl || null;
		}
		return null;
	} catch {
		return null;
	}
};

export const getPackRequirements = async (packId: string): Promise<{ data: KnowledgeRequirement[] }> => {
	const response = await fetch(getUrl("/local-knowledge/packs/" + packId + "/requirements"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

/** Preview the requirements activated by installation settings before they are saved. */
export const previewPackRequirements = async (
	packId: string,
	settings: Record<string, unknown>
): Promise<{ data: KnowledgeRequirement[] }> => {
	const response = await fetch(getUrl("/local-knowledge/packs/" + packId + "/requirements/preview"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({ settings })
	});
	if (response != null && response.ok) {
		return await response.json();
	}
	throw await responseToError(response);
};

export type InstalledPackSettings = {
	settingsFormConfig: { fields: Array<Record<string, unknown>>; layout?: "vertical" | "horizontal" };
	values: Record<string, unknown>;
};

export const getInstalledPackSettings = async (packId: string): Promise<InstalledPackSettings> => {
	const response = await fetch(getUrl("/packs/" + packId + "/install/settings"), { method: "GET", headers: await getHeaders("GET") });
	if (response != null && response.ok) return await response.json();
	throw await responseToError(response);
};

export const createTaskRequirement = async (taskId: string, data: TaskRequirementPayload): Promise<KnowledgeRequirement> => {
	const response = await fetch(getUrl("/local-knowledge/tasks/" + taskId + "/requirements"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		const result = await response.json();
		return result.requirement || result;
	} else {
		throw await responseToError(response);
	}
};

export const updateTaskRequirement = async (
	taskId: string,
	requirementId: string,
	data: UpdateTaskRequirementPayload
): Promise<KnowledgeRequirement> => {
	const response = await fetch(getUrl("/local-knowledge/tasks/" + taskId + "/requirements/" + requirementId), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		const result = await response.json();
		return result.requirement || result;
	} else {
		throw await responseToError(response);
	}
};

export const deleteTaskRequirement = async (taskId: string, requirementId: string): Promise<void> => {
	const response = await fetch(getUrl("/local-knowledge/tasks/" + taskId + "/requirements/" + requirementId), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response == null || !response.ok) {
		throw await responseToError(response);
	}
};

export const getRequirementDefaultDataUploadUrl = async (
	requirementId: string,
	data: RequirementDefaultDataUploadRequest
): Promise<{ uploadUrl: string; s3Key: string }> => {
	const response = await fetch(getUrl("/local-knowledge/requirements/" + requirementId + "/default-data-upload-url"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const confirmRequirementDefaultDataUpload = async (
	requirementId: string,
	data: ConfirmRequirementDefaultDataUploadRequest
): Promise<KnowledgeRequirement> => {
	const response = await fetch(getUrl("/local-knowledge/requirements/" + requirementId + "/confirm-default-data-upload"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		const result = await response.json();
		return result.requirement || result;
	} else {
		throw await responseToError(response);
	}
};

export const getSessionUserInput = async (
	externalSource: string,
	externalType: string,
	externalId: string,
	packId: string
): Promise<Record<string, Record<string, any>> | null> => {
	try {
		const params = new URLSearchParams();
		params.set("packId", packId);
		const response = await fetch(
			getUrl(
				`/sessions/external/${encodeURIComponent(externalSource)}/${encodeURIComponent(externalType)}/${encodeURIComponent(externalId)}/user-input?${params.toString()}`
			),
			{
				method: "GET",
				headers: await getHeaders("GET")
			}
		);
		if (response != null && response.ok) {
			return await response.json();
		}
		return null;
	} catch {
		return null;
	}
};

export interface UserInputFieldOption {
	label: string;
	value: string | number | boolean;
	record?: Record<string, unknown>;
}

/** Bounds the autocomplete lookup. See the note at the fetch below for why silence is the bad case. */
const USER_INPUT_OPTIONS_TIMEOUT_MS = 20000;

export const fetchUserInputOptions = async (
	toolName: string,
	platformCodeName: string,
	labelTemplate?: string,
	valueField?: string,
	inputParams?: Record<string, unknown>
): Promise<UserInputFieldOption[]> => {
	const params = new URLSearchParams();
	params.set("toolName", toolName);
	params.set("platformCodeName", platformCodeName);
	if (labelTemplate) params.set("labelTemplate", labelTemplate);
	if (valueField) params.set("valueField", valueField);
	if (inputParams) params.set("inputParams", JSON.stringify(inputParams));

	// Bounded because a commit that never settles is worse here than a failed one: the proceed barrier
	// (autocompleteCommitBarrier) can only drop a timed-out commit into "abandoned", and an abandoned
	// commit is removed only when it eventually settles. A request that hangs forever therefore refuses
	// every later proceed for the life of the dialog. A rejection is a state the barrier recovers from;
	// silence is not. Well above the barrier's own 5s drain, so this bounds the pathological case only.
	//
	// AbortController rather than AbortSignal.timeout: this app's browserslist declares `safari >= 15`
	// and `ios >= 15`, and AbortSignal.timeout only exists from Safari 16. On iOS 15 it is undefined, so
	// calling it throws before the request is even made and EVERY options load fails — a far worse bug
	// than the hang it was meant to bound.
	//
	// Cleared in `finally`, after the body is read, so the bound covers the body too: fetch resolving
	// means headers only, and a stalled body read hangs exactly the same way. `getHeaders` is awaited
	// BEFORE this and no signal can bound it — a token refresh that hangs is still unbounded, which is
	// why the barrier keeps its own "stuck" reporting rather than assuming this made hangs impossible.
	const controller = new AbortController();
	const lookupTimeout = setTimeout(() => controller.abort(), USER_INPUT_OPTIONS_TIMEOUT_MS);
	try {
		const response = await fetch(getUrl("/local-knowledge/user-input-options?" + params.toString()), {
			method: "GET",
			headers: await getHeaders("GET"),
			signal: controller.signal
		});
		if (response != null && response.ok) {
			const data = await response.json();
			return data.options || [];
		} else {
			throw await responseToError(response);
		}
	} catch (error) {
		// Our own abort, surfaced in our own words. The DOMException fetch rejects with reads "The user
		// aborted a request." — and this path is reached by the renderer's fieldErrors alert and by the
		// commit's LookupFailed cause, so without this the user is told THEY cancelled something they
		// never touched, and the one trace of a backend stall is a message blaming them for it.
		if (controller.signal.aborted) {
			throw new Error(`Looking up options timed out after ${USER_INPUT_OPTIONS_TIMEOUT_MS / 1000} seconds. Please try again.`);
		}
		throw error;
	} finally {
		clearTimeout(lookupTimeout);
	}
};

export const saveSessionUserInput = async (
	externalSource: string,
	externalType: string,
	externalId: string,
	packId: string,
	data: Record<string, Record<string, any>>
): Promise<void> => {
	const response = await fetch(
		getUrl(
			`/sessions/external/${encodeURIComponent(externalSource)}/${encodeURIComponent(externalType)}/${encodeURIComponent(externalId)}/user-input`
		),
		{
			method: "POST",
			headers: await getHeaders("POST"),
			body: JSON.stringify({ packId, userInputData: data })
		}
	);
	if (response != null && !response.ok) {
		throw await responseToError(response);
	}
};

export const reorderPacks = async (packs: Array<{ packId: string; position: number }>): Promise<void> => {
	const response = await fetch(getUrl("/packs/reorder"), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify({ packs })
	});
	if (response != null && !response.ok) {
		throw await responseToError(response);
	}
};

// LSP-16552: move a just-installed pack to the top of My Sparks. Used by every
// install path that does not pass an explicit drop position (plain Add clicks,
// wizard finishes, zone-drops). The reorder endpoint expects the FULL installed
// list, so this pages through every cursor page — a partial payload would corrupt
// the positions of any packs left out of it. The stuck-cursor guard and page cap
// mirror useTemplateList's author scan (a repeated nextCursor or runaway cursor
// must terminate observably, never hang), and pack ids are deduped across pages
// so an overlapping page cannot put the same packId in the payload twice.
// scribe-api's /packs route schema caps limit at 100 (rejects larger with a 400) — do not raise this above the server max.
const INSTALLED_PACK_PAGE_LIMIT = 100;
const INSTALLED_PACK_MAX_PAGES = 20; // 100 x 20 = 2000 installed packs of headroom

export const promoteInstalledPackToTop = async (packId: string): Promise<void> => {
	const seenPackIds = new Set<string>();
	const installedPacks: Array<{ packId: string; position: number }> = [];
	let cursor: string | undefined;
	let pagesFetched = 0;
	let cursorExhausted = false;
	while (pagesFetched < INSTALLED_PACK_MAX_PAGES) {
		const response = await getPacksWithFilters({
			published: false,
			sortBy: "position",
			sortOrder: "asc",
			limit: INSTALLED_PACK_PAGE_LIMIT,
			cursor
		});
		pagesFetched++;
		for (const pack of response.data) {
			if (pack.id !== packId && !seenPackIds.has(pack.id)) {
				seenPackIds.add(pack.id);
				installedPacks.push({ packId: pack.id, position: 0 });
			}
		}
		const nextCursor = response.nextCursor || undefined;
		if (!nextCursor) {
			cursorExhausted = true;
			break;
		}
		if (nextCursor === cursor) {
			console.warn("promoteInstalledPackToTop: nextCursor repeated — stopping pagination early", { packId, cursor, pagesFetched });
			break;
		}
		cursor = nextCursor;
	}
	// Reordering with a PARTIAL list would corrupt the positions of every pack the
	// payload omits (the reorder endpoint expects the full installed list), so an
	// incomplete pagination (stuck cursor above, or page cap here) skips the reorder
	// entirely — callers already treat promotion as non-fatal, and the install itself
	// is unaffected.
	if (!cursorExhausted) {
		if (pagesFetched >= INSTALLED_PACK_MAX_PAGES) {
			console.warn("promoteInstalledPackToTop: page cap reached before cursor exhaustion — skipping reorder", {
				packId,
				pagesFetched,
				installedPackCount: installedPacks.length
			});
		}
		return;
	}
	installedPacks.unshift({ packId, position: 0 });
	installedPacks.forEach((pack, index) => {
		pack.position = index;
	});
	await reorderPacks(installedPacks);
};

// Knowledge editing API functions

export const getPackAssignments = async (organizationInstalledPackId: string): Promise<{ data: KnowledgeAssignment[] }> => {
	const response = await fetch(getUrl("/local-knowledge/assignments/by-pack/" + organizationInstalledPackId), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getTableData = async (tableId: string): Promise<{ data: Record<string, any>[]; total: number }> => {
	const response = await fetch(getUrl("/local-knowledge/tables/" + tableId + "/data"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getDocumentDownloadUrl = async (
	documentId: string
): Promise<{ downloadUrl: string; filename: string | null; mimeType: string | null }> => {
	const response = await fetch(getUrl("/local-knowledge/documents/" + documentId + "/download-url"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getRequirementDefaultDataDownloadUrl = async (
	requirementId: string
): Promise<{ downloadUrl: string; filename: string; mimeType: string }> => {
	const response = await fetch(getUrl("/local-knowledge/requirements/" + requirementId + "/default-data-download-url"), {
		method: "GET",
		headers: await getHeaders("GET")
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getRequirementDefaultDownloadUrl = getRequirementDefaultDataDownloadUrl;

export const fulfillPackRequirements = async (
	organizationInstalledPackId: string,
	fulfillments: PackKnowledgeFulfillment[]
): Promise<{ message: string }> => {
	const response = await fetch(getUrl("/local-knowledge/packs/fulfill-requirements"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({ organizationInstalledPackId, fulfillments })
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const deleteKnowledgeAssignment = async (assignmentId: string): Promise<void> => {
	const response = await fetch(getUrl("/local-knowledge/assignments/" + assignmentId), {
		method: "DELETE",
		headers: await getHeaders("DELETE")
	});
	if (response == null || !response.ok) {
		throw await responseToError(response);
	}
};

export const markLocalKnowledgeReviewed = async (
	organizationInstalledPackId: string
): Promise<{ message: string; localKnowledgeReviewedAt: string }> => {
	const response = await fetch(getUrl("/local-knowledge/installed-packs/" + organizationInstalledPackId + "/local-knowledge-reviewed"), {
		method: "PUT",
		headers: await getHeaders("PUT"),
		body: JSON.stringify({})
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getTableDataUploadUrl = async (data: {
	filename: string;
	fileSize: number;
	requirementId?: string;
}): Promise<{ uploadUrl: string; s3Key: string }> => {
	const response = await fetch(getUrl("/local-knowledge/tables/upload-url"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const createDocument = async (data: { title: string; description?: string | null }): Promise<{ document: { id: string } }> => {
	const response = await fetch(getUrl("/local-knowledge/documents"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const getDocumentUploadUrl = async (data: {
	documentId: string;
	filename: string;
	mimeType: string;
	fileSize: number;
}): Promise<{ uploadUrl: string; s3Key: string }> => {
	const response = await fetch(getUrl("/local-knowledge/documents/upload-url"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify(data)
	});
	if (response != null && response.ok) {
		return await response.json();
	} else {
		throw await responseToError(response);
	}
};

export const confirmDocumentUpload = async (documentId: string): Promise<{ message: string; documentId: string }> => {
	const response = await fetch(getUrl("/local-knowledge/documents/" + documentId + "/confirm-upload"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({})
	});
	if (response != null && response.ok) {
		try {
			return await response.json();
		} catch (error) {
			console.warn("confirmDocumentUpload: upload was confirmed but the success response could not be parsed", {
				documentId,
				error
			});
			return { message: "confirmed (response body could not be parsed)", documentId };
		}
	} else {
		throw await responseToError(response);
	}
};

/**
 * F-16 (media-upload resilience review): bounded in-pass retries for a scribe presigned PUT.
 *
 * Deliberately NOT an R-0 durable lane. The dialog that calls this is still open and still holds the
 * bytes, and Save is the user's own retry — so the right shape here is "survive a blip without
 * bothering the user", not "persist an outbox row". Three attempts is enough to ride out a dropped
 * connection or a 5xx; anything beyond that is a real failure the user should see.
 */
const SCRIBE_UPLOAD_MAX_ATTEMPTS = 3;

/**
 * F-16: route scribe local-knowledge / Spark-guide uploads through the app's canonical presigned-PUT
 * transport instead of a bare single `fetch`.
 *
 * What the bare fetch was missing, all of which this now inherits from `putToS3WithStallTimeout`
 * (`helpers/s3.ts` — the same transport the chunked and snapshot paths use):
 *  - a zero-progress stall abort, so a hung PUT fails in a minute instead of hanging forever;
 *  - the parsed S3 error (code / status / requestId) instead of the string "Failed to upload file",
 *    which told support nothing;
 *  - retry on the failures that are worth retrying, classified by the shared `classifyUploadFailure`
 *    rather than by a hand-rolled status check, on the shared jittered-exponential curve.
 *
 * A terminal classification (a 4xx, a cancellation) is rethrown immediately — retrying an expired or
 * refused presigned URL only wastes the user's time.
 */
export const uploadToPresignedUrl = async (url: string, body: Blob | string, contentType?: string): Promise<void> => {
	const headers: Record<string, string> = {};
	if (contentType) {
		headers["Content-Type"] = contentType;
	}
	// The transport is Blob-based; the string overload is part of this function's existing contract.
	const payload = typeof body === "string" ? new Blob([body], contentType ? { type: contentType } : undefined) : body;

	for (let attempt = 0; attempt < SCRIBE_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
		try {
			const response = await putToS3WithStallTimeout(payload, url, { headers });
			if (response.ok) return;
			throw await parseS3XmlError(response);
		} catch (error) {
			const classification = classifyUploadFailure(error, attempt);
			const status = (error as { status?: unknown } | null)?.status;
			const terminal4xx = typeof status === "number" && status >= 400 && status < 500 && ![408, 425, 429].includes(status);
			const retryable = classification.retryable && !terminal4xx;
			const isFinalAttempt = attempt === SCRIBE_UPLOAD_MAX_ATTEMPTS - 1;
			// Silent-failure rule (root CLAUDE.md): every exit from this loop names WHY, with the attempt
			// number and the classification, so a failed Save is triagable from one log line instead of
			// the previous opaque "Failed to upload file".
			if (!retryable || isFinalAttempt) {
				console.warn(
					`[scribe] presigned upload failed: attempt=${attempt + 1}/${SCRIBE_UPLOAD_MAX_ATTEMPTS}, ` +
						`kind=${classification.kind}, retryable=${retryable}, bytes=${payload.size}, ` +
						`contentType=${contentType ?? "unset"}`,
					error
				);
				throw error;
			}
			const retryAt = computeRetryAt({ attempt, nowMs: Date.now() });
			console.warn(
				`[scribe] presigned upload retrying: attempt=${attempt + 1}/${SCRIBE_UPLOAD_MAX_ATTEMPTS}, ` +
					`kind=${classification.kind}, bytes=${payload.size}`,
				error
			);
			if (retryAt !== null) await new Promise((resolve) => setTimeout(resolve, Math.max(0, retryAt - Date.now())));
		}
	}
	// Unreachable: the loop's final iteration always throws. Kept so the function can never fall
	// through and report success for an upload that never happened.
	throw new Error("Scribe presigned upload exhausted its attempts without a verdict");
};

export const installDefaultTemplates = async (categoryCodeNames: string[]): Promise<void> => {
	const response = await fetch(getUrl("/packs/default-templates/install"), {
		method: "POST",
		headers: await getHeaders("POST"),
		body: JSON.stringify({ categoryCodeNames, templateKind: "all" })
	});
	if (response != null && response.ok) {
		return;
	}
	throw await responseToError(response);
};
